import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { Plus, Pencil, Trash2 } from "lucide-react";

const FIELDS = ["{naam}", "{debiteur}", "{opdrachtgever}", "{contactpersoon}", "{adres}", "{email}", "{datum}"];

export default function ContractTemplatesDialog({ open, onOpenChange }) {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/contract-templates").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { if (open) { load(); setEdit(null); } }, [open]);

  const save = async () => {
    try {
      if (edit.id) await api.put(`/contract-templates/${edit.id}`, { name: edit.name, content: edit.content });
      else await api.post("/contract-templates", { name: edit.name, content: edit.content });
      toast.success("Sjabloon opgeslagen"); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Opslaan mislukt"); }
  };
  const del = async (t) => { await api.delete(`/contract-templates/${t.id}`); toast.success("Sjabloon verwijderd"); load(); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="templates-dialog">
        <DialogHeader><DialogTitle>Contractsjablonen</DialogTitle><DialogDescription>Eigen sjablonen voor elk soort contract. Gebruik velden die automatisch worden ingevuld.</DialogDescription></DialogHeader>
        {edit ? (
          <div className="space-y-3">
            <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder="Naam van het sjabloon" data-testid="template-name" />
            <div className="flex flex-wrap gap-1.5">{FIELDS.map((f) => <button key={f} type="button" className="text-[11px] px-2 py-1 rounded-full border border-[#C9A227]/60 text-[#0F1E3D] hover:bg-[#C9A227]/10" onClick={() => setEdit({ ...edit, content: `${edit.content}${f}` })}>{f}</button>)}</div>
            <Textarea value={edit.content} onChange={(e) => setEdit({ ...edit, content: e.target.value })} rows={14} className="text-sm font-mono" placeholder="Tekst van de overeenkomst…" data-testid="template-content" />
            <div className="flex justify-end gap-2"><Button variant="ghost" onClick={() => setEdit(null)}>Annuleren</Button><Button onClick={save} disabled={!edit.name.trim() || !edit.content.trim()} data-testid="template-save">Opslaan</Button></div>
          </div>
        ) : (
          <div className="space-y-2">
            {list.length === 0 && <p className="text-sm text-slate-400 text-center py-6">Nog geen sjablonen.</p>}
            {list.map((t) => (
              <div key={t.id} className="flex items-center justify-between border rounded-lg px-3 py-2" data-testid={`template-row-${t.id}`}>
                <span className="font-medium text-[#0F1E3D] truncate">{t.name}</span>
                <div className="flex gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => setEdit(t)} data-testid={`template-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" className="text-red-500" onClick={() => del(t)} data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                </div>
              </div>
            ))}
            <Button className="w-full" onClick={() => setEdit({ name: "", content: "" })} data-testid="template-new"><Plus className="h-4 w-4 mr-2" /> Nieuw sjabloon</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
